// === Admin: Client Management ===
// Superadmin-only. Lists every company for platform administration.
//
// Entering a company's data is deliberately NOT done from here any more. It
// lives in the web app: sign in there and pick a company at /select-company.
// Driving it from this page meant a second tab with a different identity, and
// the two views constantly disagreed about who you were and which company you
// were looking at. One app, one tab, one session.

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Fingerprint as FingerprintIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLAN_LABELS: Readonly<Record<number, string>> = { 0: 'Free', 1: 'Essential', 2: 'Standard', 3: 'Premium' };
type PlanColor = 'default' | 'info' | 'success' | 'warning';
const PLAN_COLORS: Readonly<Record<number, PlanColor>> = { 0: 'default', 1: 'info', 2: 'success', 3: 'warning' };

type StripeStatusColor = 'success' | 'warning' | 'error' | 'info' | 'default';
const STRIPE_STATUS_COLORS: Readonly<Record<string, StripeStatusColor>> = {
  active:             'success',
  trialing:           'info',
  past_due:           'warning',
  canceled:           'error',
  unpaid:             'error',
  incomplete_expired: 'error',
  incomplete:         'default',
  paused:             'default',
};

// ---------------------------------------------------------------------------
// Types — match the shape returned by GET /api/v1/admin/companies
// ---------------------------------------------------------------------------

interface CompanyRow {
  readonly id:             string;
  readonly company_name:   string;
  readonly email_address:  string | null;
  readonly first_name:     string | null;
  readonly last_name:      string | null;
  readonly phone_no:       string | null;
  readonly mobile_number:  string | null;
  readonly suburb_name:    string | null;
  readonly plan_type:      number;
  readonly status:         string | null;
  readonly stripe_status:  string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function contactName(row: CompanyRow): string {
  return [row.first_name, row.last_name].filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientManagementPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  const [rows, setRows] = useState<readonly CompanyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);


  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // Company-ID viewer dialog
  const [idDialogRow, setIdDialogRow] = useState<CompanyRow | null>(null);
  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      showSnack('Company ID copied to clipboard');
    } catch {
      showSnack('Failed to copy Company ID', 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Fetch list
  // -------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/companies`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      // Hide soft-deleted (default) AND decommissioned tenants — neither can
      // be impersonated, so showing them in this picker is just noise.
      url.searchParams.set('active_only', 'true');
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);
      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: CompanyRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton onClick={() => navigate('/dashboard')} size="small">
          <BackIcon />
        </IconButton>
        <BusinessIcon color="primary" />
        <Typography variant="h5" fontWeight={600} flexGrow={1}>
          Client Management
        </Typography>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} p={2}>
          <TextField
            size="small"
            placeholder="Search companies…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 320 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Box flexGrow={1} />
          <Typography variant="caption" color="text.secondary">
            To work inside a company's data, sign in to the web app and choose the company there.
          </Typography>
        </Box>
      </Paper>

      {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <AppTableHead>
              <TableRow>
                <AppTableHeadCell>Company</AppTableHeadCell>
                <AppTableHeadCell>Email</AppTableHeadCell>
                <AppTableHeadCell>Contact</AppTableHeadCell>
                <AppTableHeadCell>Suburb</AppTableHeadCell>
                <AppTableHeadCell>Plan</AppTableHeadCell>
                <AppTableHeadCell>Status</AppTableHeadCell>
                <AppTableHeadCell align="right">Action</AppTableHeadCell>
              </TableRow>
            </AppTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No companies found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  return (
                    <AppTableRow key={row.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{row.company_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.email_address ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{contactName(row) || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.suburb_name ?? '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={PLAN_LABELS[row.plan_type] ?? String(row.plan_type)}
                          color={PLAN_COLORS[row.plan_type] ?? 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {row.stripe_status ? (
                          <Chip
                            label={row.stripe_status.replace(/_/g, ' ')}
                            color={STRIPE_STATUS_COLORS[row.stripe_status] ?? 'default'}
                            size="small"
                            variant="filled"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        ) : row.status ? (
                          <Chip
                            label={row.status}
                            color={row.status === 'paid' ? 'success' : 'default'}
                            size="small"
                            variant="filled"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Show Company ID">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setIdDialogRow(row)}
                            >
                              <FingerprintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </AppTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: theme.palette.mode === 'light'
            ? alpha(theme.palette.primary.main, 0.09)
            : darken(theme.palette.background.paper, 0.35),
          px: 2,
          py: 1.5,
        })}>
          <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
            {loading ? 'Loading…' : total === 0 ? 'No records found'
              : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                  '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                  '& .MuiSelect-select': { py: 0.5, px: 1 },
                })}
              >
                {ROWS_PER_PAGE_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </Box>
            {totalPages > 0 && (
              <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                Page {page + 1} of {totalPages}
              </Typography>
            )}
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(_, p) => setPage(p - 1)}
                size="small"
                disabled={loading}
                sx={(theme) => ({
                  '& .MuiPaginationItem-root': {
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  },
                  '& .Mui-selected': {
                    backgroundColor: `${theme.palette.primary.main} !important`,
                    color: theme.palette.primary.contrastText,
                  },
                  '& .Mui-disabled': { opacity: 0.5 },
                })}
              />
            )}
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

      <Dialog open={idDialogRow !== null} onClose={() => setIdDialogRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Company ID</DialogTitle>
        <DialogContent>
          {idDialogRow && (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                {idDialogRow.company_name}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1 }}
                >
                  {idDialogRow.id}
                </Typography>
                <Tooltip title="Copy">
                  <IconButton size="small" onClick={() => void copyId(idDialogRow.id)}>
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdDialogRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
